// The fleet hour by hour: what each turbine could make, what it makes, and why it makes less.
import { rng, hashStr, gauss, clamp } from './util.js';
import { FLEET, TURB, FAULT } from './content.js';
import { WX, H, hourDate } from './weather.js';
import { powerKW, thrust, rotorRpm, stormControl, layout, wakeDeficits, modelPrice, faultPower } from './physics.js';

export var FARMS = [], FARM = {}, TURBS = [], TUR = {};
export var SIM = null;            // last simulation result
export var LOSS_KEYS = ['storm', 'ice', 'faults', 'service', 'derate', 'grid', 'price'];

export function buildFleet() {
  FARMS = []; FARM = {}; TURBS = []; TUR = {};
  FLEET.farms.forEach(function (f) {
    var farm = Object.assign({}, f, { pos: layout(f), turbines: [], ratedKW: f.n * TURB.ratedKW });
    FARMS.push(farm); FARM[f.id] = farm;
    for (var k = 0; k < f.n; k++) {
      var id = f.id + '-' + (k < 9 ? '0' : '') + (k + 1), r = rng(hashStr(id));
      var t = { id: id, farm: farm, idx: k, pos: farm.pos[k], terrain: 1 + (r() - 0.5) * 0.05, issue: null, outage: null };
      farm.turbines.push(t); TURBS.push(t); TUR[id] = t;
    }
  });
  FLEET.issues.forEach(function (x) { var t = TUR[x.turbine]; if (t) t.issue = Object.assign({ f: FAULT[x.fault] }, x); });
  FLEET.outages.forEach(function (x) { var t = TUR[x.turbine]; if (t) t.outage = x; });
}

// opts: { hwrt, curtailNeg, derate: turbine id or null, plans: { turbineId: [startHour, endHour] }, holiday }
export function simulate(opts) {
  var n = H, wakeCache = {}, out = { hours: n, price: new Float32Array(n), cf: new Float32Array(n), congested: new Uint8Array(n), farm: {}, fleet: null, tur: {} };
  // 1. available power per turbine (physics, storm control, icing, faults)
  TURBS.forEach(function (t) {
    var f = t.farm, w = WX[f.id].hours, v = new Float32Array(n), vf = new Float32Array(n), g = [], pIdeal = new Float32Array(n), pWake = new Float32Array(n);
    for (var i = 0; i < n; i++) {
      var x = w[i], key = f.id + '|' + Math.round(x.dir / 5) + '|' + Math.round(thrust(x.v) * 20), def = wakeCache[key];
      if (!def) def = wakeCache[key] = wakeDeficits(f.pos, Math.round(x.dir / 5) * 5, thrust(x.v));
      vf[i] = x.v * t.terrain; v[i] = vf[i] * (1 - def[t.idx]);
      g.push(x.g * t.terrain * (1 - def[t.idx] * 0.5));
      pIdeal[i] = powerKW(vf[i], x.rho); pWake[i] = powerKW(v[i], x.rho);
    }
    var sc = stormControl(v, g, opts.hwrt);
    var T = { v: v, vf: vf, g: g, pIdeal: pIdeal, pWake: pWake, P: new Float32Array(n), av: new Float32Array(n), rpm: new Float32Array(n), code: new Array(n), loss: {} };
    LOSS_KEYS.forEach(function (k) { T.loss[k] = new Float32Array(n); });
    for (i = 0; i < n; i++) {
      var x0 = pWake[i], share = sc[i].share, x1 = x0 * share, ice = w[i].ice, code = v[i] < TURB.cutIn ? 'idle' : sc[i].mode;
      T.loss.storm[i] = x0 - x1;
      var iceLoss = ice >= 0.85 ? 1 : 0.55 * ice, x2 = x1 * (1 - iceLoss);
      T.loss.ice[i] = x1 - x2; if (ice >= 0.85 && x1 > 0) code = 'ice';
      var x3 = t.issue ? faultPower(t.issue, v[i], w[i].rho, x2) : x2; T.loss.faults[i] = x2 - x3;
      T.av[i] = x3; T.P[i] = x3; T.code[i] = code;
      if (t.outage && i < t.outage.untilHour) { T.loss.service[i] = x3; T.P[i] = 0; T.code[i] = 'service'; }
      var pl = opts.plans && opts.plans[t.id]; if (pl && i >= pl[0] && i < pl[1]) { T.loss.service[i] = x3; T.P[i] = 0; T.code[i] = 'service'; }
      if (opts.derate === t.id && T.P[i] > 0.7 * TURB.ratedKW) { T.loss.derate[i] = T.P[i] - 0.7 * TURB.ratedKW; T.P[i] = 0.7 * TURB.ratedKW; if (T.code[i] === 'run') T.code[i] = 'derate'; }
    }
    out.tur[t.id] = T;
  });
  // 2. market and grid
  var r = rng(hashStr('price' + T0key())), north = FARMS.filter(function (f) { return f.grid === 'north'; });
  for (var i = 0; i < n; i++) {
    var s = 0, cap = 0, sn = 0, capn = 0, sun = 0;
    FARMS.forEach(function (f) { f.turbines.forEach(function (t) { s += out.tur[t.id].av[i]; }); cap += f.ratedKW; sun += WX[f.id].hours[i].sun * (1 - 0.7 * WX[f.id].hours[i].cloud / 100); });
    north.forEach(function (f) { f.turbines.forEach(function (t) { sn += out.tur[t.id].av[i]; }); capn += f.ratedKW; });
    var d = hourDate(i);
    out.cf[i] = s / cap;
    out.price[i] = modelPrice(s / cap, sun / FARMS.length, d.getHours(), d.getDay(), opts.holiday, gauss(r) * 4);
    out.congested[i] = sn / capn > 0.72 ? 1 : 0;
  }
  // 3. curtailment: grid (north, redispatch to 60 %) and negative prices (§ 51 EEG)
  TURBS.forEach(function (t) {
    var T = out.tur[t.id];
    for (var i = 0; i < n; i++) {
      if (out.congested[i] && t.farm.grid === 'north' && T.P[i] > 0.6 * TURB.ratedKW) { T.loss.grid[i] = T.P[i] - 0.6 * TURB.ratedKW; T.P[i] = 0.6 * TURB.ratedKW; T.code[i] = 'grid'; }
      if (opts.curtailNeg && out.price[i] < 0 && T.P[i] > 0) { T.loss.price[i] = T.P[i]; T.P[i] = 0; T.code[i] = 'price'; }
      T.rpm[i] = T.P[i] > 0 ? rotorRpm(T.v[i]) * (T.code[i] === 'grid' || T.code[i] === 'derate' || T.code[i] === 'hwrt' ? 0.92 : 1) : (T.code[i] === 'idle' ? 0.6 : 0);
    }
  });
  // 4. totals per farm and for the fleet
  function blank() { var o = { P: new Float32Array(n), av: new Float32Array(n), ideal: new Float32Array(n), wake: new Float32Array(n), loss: {} }; LOSS_KEYS.forEach(function (k) { o.loss[k] = new Float32Array(n); }); return o; }
  var fl = blank();
  FARMS.forEach(function (f) {
    var a = blank(); a.rated = f.ratedKW;
    f.turbines.forEach(function (t) {
      var T = out.tur[t.id];
      for (var i = 0; i < n; i++) {
        a.P[i] += T.P[i]; a.av[i] += T.pWake[i]; a.ideal[i] += T.pIdeal[i]; a.wake[i] += T.pIdeal[i] - T.pWake[i];
        LOSS_KEYS.forEach(function (k) { a.loss[k][i] += T.loss[k][i]; });
      }
    });
    for (var i = 0; i < n; i++) { fl.P[i] += a.P[i]; fl.av[i] += a.av[i]; fl.ideal[i] += a.ideal[i]; fl.wake[i] += a.wake[i]; LOSS_KEYS.forEach(function (k) { fl.loss[k][i] += a.loss[k][i]; }); }
    out.farm[f.id] = a;
  });
  fl.rated = TURBS.length * TURB.ratedKW; out.fleet = fl;
  if (!opts.dry) SIM = out;
  return out;
}

function T0key() { var d = hourDate(0); return d.getFullYear() + '-' + d.getMonth() + '-' + d.getDate(); }

// energy (MWh) and value (EUR) of a power series over hours [a, b)
export function energy(arr, a, b) { var s = 0; for (var i = a; i < b; i++) s += arr[i]; return s / 1000; }
export function value(arr, a, b) { var s = 0; for (var i = a; i < b; i++) s += arr[i] / 1000 * SIM.price[i]; return s; }
export function clampH(h) { return clamp(h | 0, 0, H - 1); }
